import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFetchForms } from '../hooks/useFetchForms';
import { useDeleteFormMutation } from '../services/api';
import { setEditFormModal } from '../store/actions/modals';
import { SectionLoader } from './general/Loaders';
import { Edit2, Trash2, Link2 } from 'lucide-react';
import { successToast, errorToast } from '../utils/toasts';

const Toast = ({ show }) => {
  if (!show) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50">
      <span>Link copied to clipboard!</span>
    </div>
  );
};

export const Forms = () => {
  const dispatch = useDispatch();

  const [showToast, setShowToast] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  const { forms, isLoading } = useFetchForms();

  const [deleteFormTrigger] = useDeleteFormMutation();

  const handleEditForm = (formId, e) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();

    dispatch(setEditFormModal({ showModal: true, formId: formId }));
  };

  const handleRemoveForm = (formId, e) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();

    setFormToDelete(formId);
  };

  const confirmDelete = () => {
    if (!formToDelete) return;

    deleteFormTrigger({ id: formToDelete })
      .unwrap()
      .then(_ => {
        successToast('Form deleted successfully!');
      })
      .catch(error => {
        console.error(error);
        errorToast('Unable to delete form. Please try again.');
      })
      .finally(() => {
        setFormToDelete(null);
      });
  };

  const cancelDelete = () => {
    setFormToDelete(null);
  };

  const handleCopyLink = (formId, e) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();

    const link = `${window.location.origin}/forms/${formId}`;
    navigator.clipboard.writeText(link).then(() => {
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 my-12">
        <SectionLoader dotColor="slate" />
        <p>Loading feedback forms...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex flex-col items-center mb-12">
          <h2 className="text-4xl md:text-5xl font-light text-black mb-4">All feedback forms</h2>
          <p className="text-xl text-gray-600 font-light">
            {forms.length} submission{forms.length > 1 || !forms.length ? 's' : ''}
          </p>
        </div>

        <div className="flex flex-col mx-6">
          <div className="space-y-4">
            {forms.map(form => (
              <NavLink
                key={form.id}
                to={`/forms/${form.id}`}
                className="no-underline font-normal bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:cursor-pointer transition-shadow block"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">{form.site_name}</h3>
                        <p className="text-sm text-gray-600">{form.site_url}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={e => handleEditForm(form.id, e)}
                          className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                          aria-label="Edit form"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={e => handleRemoveForm(form.id, e)}
                          className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                          aria-label="Remove form"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                        <span>{form.submissions?.length} submissions</span>
                        <span>•</span>
                        <span>{form.subscribers?.length} subscribers</span>
                        <span>•</span>
                        <span>
                          Last updated: <em>{form.modified_date}</em>
                        </span>
                      </div>
                      <button
                        onClick={e => handleCopyLink(form.id, e)}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
                        title="Copy link"
                      >
                        <Link2 className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-600 font-medium">Copy link</span>
                      </button>
                    </div>
                  </div>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {formToDelete && (
        <div className="fixed inset-0 bg-gray-700/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Delete Form</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this form and all its associated submissions? This
              action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded bg-error text-white hover:opacity-80 transition-opacity"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={showToast} />
    </div>
  );
};
